import React, {
  type ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { KibDateField } from '@chewy/kib-calendar-react'
import {
  KibCheckbox,
  type KibCheckboxModelValue,
} from '@chewy/kib-choices-react'
import { KibField } from '@chewy/kib-fields-new-react'
import { moment } from '@pbt/pbt-ui-components'

import type { DataHandle, Prescription } from '~/types'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import '@chewy/kib-calendar-styles/dist/kib-calendar.css'
import style from './SwapProductRefill.module.scss'

export interface SwapProductRefillProps {
  onRefChange?: () => void
  prescription: Prescription
}

export interface SwapProductRefillHandle extends DataHandle {}

const SwapProductRefill = forwardRef<
  SwapProductRefillHandle,
  SwapProductRefillProps
>(function SwapProductRefill({ onRefChange, prescription }, ref) {
  const { t } = useTranslation(['Common', 'Dialogs', 'Tooltips', 'Tasks'])

  const [isRefillable, setIsRefillable] = useState<KibCheckboxModelValue>(
    Boolean(prescription.quantityNumbRefills),
  )
  const [refillableQuantity, setRefillableQuantity] = useState<
    number | undefined
  >(prescription.quantityNumbRefills || 1)
  const [refillExpirationDate, setRefillExpirationDate] = useState<string>(
    prescription.quantityRefillExpirationDate
      ? moment(prescription.quantityRefillExpirationDate).format('MM/DD/YYYY')
      : '',
  )
  const [dateError, setDateError] = useState<string>('')

  const validateRefillExpirationDate = (
    expirationDate: string | undefined,
    creationDate: string | undefined,
  ) => {
    if (!expirationDate || !creationDate) {
      setDateError('')
      return true
    }

    const expirationMoment = moment(expirationDate, 'MM/DD/YYYY', true)
    if (!expirationMoment.isValid()) {
      setDateError(t('Common:INVALID_DATE_FORMAT'))
      return false
    }

    const oneYearFromCreation = moment(creationDate).add(1, 'years')
    const isValid = expirationMoment.isSameOrBefore(oneYearFromCreation)

    if (!isValid) {
      setDateError(
        t(
          'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILL_EXPIRATION_MAX_ONE_YEAR',
        ),
      )
    } else {
      setDateError('')
    }

    return isValid
  }

  useImperativeHandle(
    ref,
    () => ({
      get: () => {
        // Convert date from MM/DD/YYYY to ISO format for backend
        const expirationDateISO =
          isRefillable && refillExpirationDate
            ? moment(refillExpirationDate, 'MM/DD/YYYY').toISOString()
            : null

        return {
          quantityNumbRefills: isRefillable
            ? Number(refillableQuantity) || 0
            : 0,
          quantityRefillExpirationDate: expirationDateISO,
        }
      },
      validate: () => {
        if (isRefillable) {
          if (!refillableQuantity || refillableQuantity < 1) return false
          if (!refillExpirationDate) return false
          if (
            !validateRefillExpirationDate(
              refillExpirationDate,
              prescription.createdAt,
            )
          )
            return false
        }
        return true
      },
    }),
    [isRefillable, refillExpirationDate, refillableQuantity],
  )

  useEffectExceptOnMount(() => {
    setIsRefillable(Boolean(prescription.quantityNumbRefills))
    setRefillableQuantity(prescription.quantityNumbRefills || 1)
    setRefillExpirationDate(
      prescription.quantityRefillExpirationDate
        ? moment(prescription.quantityRefillExpirationDate).format('MM/DD/YYYY')
        : '',
    )
    setDateError('')
  }, [
    prescription.quantityNumbRefills,
    prescription.quantityRefillExpirationDate,
  ])

  useEffect(() => {
    onRefChange?.()
  }, [isRefillable, refillableQuantity, refillExpirationDate, onRefChange])
  return (
    <div className={style.refillable}>
      <KibCheckbox
        label={t('Dialogs:PRESCRIPTION_DIALOG:QUANTITY_SECTION:REFILLABLE')}
        selectedValue={isRefillable}
        onChange={(val) => setIsRefillable(val)}
      />
      {isRefillable && (
        <>
          <KibField
            required
            label={t(
              'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
            )}
            min="1"
            type="number"
            value={refillableQuantity}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              setRefillableQuantity(
                value === '' ? undefined : parseInt(value, 10),
              )
            }}
          />
          <KibDateField
            showCalendar
            className={style.dateFieldPlaceholder}
            format="MM/dd/yyyy"
            help={dateError}
            label={t(
              'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILL_EXPIRATION',
            )}
            placeholder="mm/dd/yyyy"
            value={refillExpirationDate}
            onChange={(e) => {
              const newValue = e.target.value
              setRefillExpirationDate(newValue)
              validateRefillExpirationDate(newValue, prescription.createdAt)
            }}
          />
        </>
      )}
    </div>
  )
})

export default SwapProductRefill
